import { useEffect } from 'react';

import { isTauri } from '@/tauri/utils';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';

import { getEffectiveAuthProvider } from '../effectiveAuthProvider';
import { getAccessToken } from '../getAccessToken';

/**
 * Refresh trigger + bootstrap prewarm for the auth token cache.
 *
 * Mount ONCE in the full-app provider tree, **above** `<AuthWrapper>`.
 * The bootstrap prewarm must run before any AuthWrapper-gated child
 * mounts, otherwise the gate (`useIsAuthenticated().isAuthenticated`,
 * which is `false` while `authStateAtom === 'loading'`) holds the
 * children indefinitely.
 *
 * Do NOT mount in `MinimalProviders` — overlay windows shouldn't drive
 * the main session's refresh cadence.
 *
 * Replaces the refresh-trigger role of `useAuth0Session` (which used
 * TanStack Query `refetchOnWindowFocus`). Provider-neutral by design —
 * does not branch on `state.auth.authProvider`; `getAccessToken` does
 * the per-provider dispatch.
 *
 * ## Boot-time transient retry
 *
 * If the very first fetch on app load fails with a transient error
 * (network, timeout, 5xx), the cache leaves `authStateAtom` at
 * `'loading'` by design — preserving previously-cached state across a
 * blip is preferable to a spurious logout. The scheduler runs a
 * bounded backoff retry (1s / 3s / 10s) so a brief outage recovers
 * automatically instead of waiting for the next window-focus event.
 * If all three retries fail, the state STAYS at `'loading'` per the
 * cache contract — sustained-outage UX is the spec-accepted trade-off.
 */

const BOOT_TRANSIENT_BACKOFFS_MS = [1_000, 3_000, 10_000] as const;

/**
 * Spreads each backoff delay by ±20% to break wall-clock synchronisation
 * across the fleet. Without jitter, a deployment-wide transient (e.g. a
 * 5xx blip on /api/auth0-session) hits every client booting in the same
 * window and produces three synchronised retry bursts at exactly +1s /
 * +3s / +10s — exactly the thundering herd jitter exists to prevent.
 * The per-tab inFlight dedup caps each client at one fetch per attempt;
 * jitter handles the cohort-level dimension.
 */
function jitter(ms: number): number {
  return ms * (0.8 + Math.random() * 0.4);
}

export function useAuthTokenCacheScheduler(): null {
  useEffect(() => {
    let cancelled = false;

    // Per-trigger Kinde-web check (NOT a whole-effect early-return).
    // If we exited the effect on a Kinde-web mount, a same-tab migration
    // that flips the cookie to AUTH0 mid-session would never get focus-
    // based cache warming back — the listener was never attached and
    // useEffect with empty deps doesn't re-run. So we always attach the
    // listener and gate per-event; the migration flow drives the cache
    // through useKindeMigrationCompletion's refreshSession() at flip
    // time, and from the next focus onward this scheduler resumes
    // refreshing automatically.
    const runWithBootRetry = async (): Promise<void> => {
      // Skip only the bootstrap prewarm — not the focus listener below —
      // when mounting as Kinde-web. The stub /api/kinde-session would
      // bare-null the cache and drive authStateAtom to 'unauthenticated'
      // for users who are actually signed in via Kinde; useIsAuthenticated
      // falls back to useKinde for that provider until the sibling
      // endpoint ships.
      if (isKindeWebUser()) return;

      await getAccessToken();
      if (cancelled) return;

      await runBootTransientRetry({ isCancelled: () => cancelled });
    };

    void runWithBootRetry();

    const onFocus = (): void => {
      // Re-check per event so the gate moves with the cookie. Kinde-web
      // stays a no-op; once the migration flips the cookie to AUTH0 the
      // very next focus event drives the cache through the Auth0 branch.
      if (isKindeWebUser()) return;
      void getAccessToken();
    };
    window.addEventListener('focus', onFocus);

    // Reconnect trigger. TanStack Query's `refetchOnReconnect` default
    // is on and fires app-shell queries as soon as `navigator.onLine`
    // flips true — with a mirror-written but expired token the first
    // 401 hits `debouncedExpiryLogout` (no refresh attempt in that
    // handler yet), destructively logging out users who still hold a
    // valid refresh token. Warming the cache on the `online` event
    // gets a fresh token into Redux before / alongside TanStack's own
    // reconnect handling, closing the race in practice. Same
    // per-trigger Kinde-web gate as focus — the listener attaches
    // unconditionally so a same-tab Kinde→Auth0 migration doesn't
    // silently disable this trigger.
    const onOnline = (): void => {
      if (isKindeWebUser()) return;
      void getAccessToken();
    };
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  return null;
}

function isKindeWebUser(): boolean {
  // Cookie-first via getEffectiveAuthProvider so the migration window
  // (cookie=AUTH0, Redux=KINDE) doesn't keep skipping the prewarm —
  // the dispatcher would already route to Auth0 web in that state.
  return !isTauri() && getEffectiveAuthProvider() === AuthProvider.KINDE;
}

async function runBootTransientRetry({
  isCancelled,
}: {
  isCancelled: () => boolean;
}): Promise<void> {
  // Lazy import so jest module mocks for `@heidi/auth/state` work in tests
  // without needing to mock this module too.
  const { getDefaultStore } = await import('jotai');
  const { authStateAtom, authTransientFailureAtom } = await import('@heidi/auth/state');
  const store = getDefaultStore();

  const shouldRetry = (): boolean =>
    store.get(authStateAtom) === 'loading' &&
    store.get(authTransientFailureAtom) === true;

  if (!shouldRetry()) {
    return;
  }

  for (const delayMs of BOOT_TRANSIENT_BACKOFFS_MS) {
    await wait(jitter(delayMs));
    if (isCancelled()) return;
    if (!shouldRetry()) return;

    await getAccessToken();
    if (isCancelled()) return;
    if (!shouldRetry()) return;
  }

  // Retries exhausted and the cache is still in the transient state.
  // Leave authStateAtom at 'loading' per the cache contract — the
  // window-focus trigger will retry the next time the user interacts.
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
